"use client"

import * as React from "react"
import { useGSAP } from "@gsap/react"
import gsap from "gsap"
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PauseIcon,
  PlayIcon,
} from "lucide-react"

import { cn } from "@workspace/ui/lib/utils"

gsap.registerPlugin(useGSAP)

/** How long a slide holds before the next, in ms. */
const DEFAULT_INTERVAL_MS = 7000
/** Slide transition, in seconds. */
const SLIDE_S = 0.6
/** Horizontal travel, in px, that counts as a swipe. */
const SWIPE_PX = 48

export interface AnnouncementCarouselLabels {
  previous: string
  next: string
  pause: string
  play: string
  /** Dot label; `{index}` is replaced with the 1-based slide number. */
  goTo: string
  /** Slide label; `{index}` and `{total}` are replaced. */
  slide: string
}

/**
 * One announcement panel at a time, sliding with GSAP, for when there are
 * several. The panels arrive server-rendered as `children`, one per slide.
 *
 * Follows the WAI-ARIA carousel pattern:
 *   - previous / next buttons, a dot per slide and a pause/play toggle; on
 *     touch screens a horizontal swipe also moves it;
 *   - autoplay holds while the pointer is over it or focus is inside it, and
 *     never starts under `prefers-reduced-motion` (slides then change
 *     instantly, by hand only);
 *   - slides out of view are `inert`, so neither focus nor screen readers
 *     reach them, and the slide region is announced politely only while
 *     autoplay is off — a rotating region that kept talking would be noise.
 *
 * All slides share the tallest one's height, so the page below never jumps.
 * Direction-aware: in RTL the next slide comes from the left.
 */
export function AnnouncementCarousel({
  children,
  label,
  labels,
  intervalMs = DEFAULT_INTERVAL_MS,
  className,
  ...props
}: Omit<React.ComponentProps<"section">, "children"> & {
  children: React.ReactNode
  /** Accessible name of the carousel, e.g. "Announcements". */
  label: string
  labels: AnnouncementCarouselLabels
  intervalMs?: number
}) {
  const slides = React.Children.toArray(children)
  const total = slides.length
  const rootRef = React.useRef<HTMLElement>(null)
  const trackRef = React.useRef<HTMLDivElement>(null)
  const swipeRef = React.useRef<number | null>(null)
  const [index, setIndex] = React.useState(0)
  const [paused, setPaused] = React.useState(false)
  const [held, setHeld] = React.useState(false)
  const [motionOk, setMotionOk] = React.useState(false)

  useGSAP(() => {
    const mm = gsap.matchMedia()
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      setMotionOk(true)
      return () => setMotionOk(false)
    })
    return () => mm.revert()
  })

  const go = (next: number) => setIndex(((next % total) + total) % total)

  useGSAP(
    () => {
      const root = rootRef.current
      const track = trackRef.current
      if (!root || !track) return
      const direction = getComputedStyle(root).direction === "rtl" ? 1 : -1
      gsap.to(track, {
        xPercent: direction * 100 * index,
        duration: motionOk ? SLIDE_S : 0,
        ease: "power3.inOut",
      })
    },
    { scope: rootRef, dependencies: [index, motionOk] }
  )

  useGSAP(
    () => {
      if (total < 2 || !motionOk || paused || held) return
      gsap.delayedCall(intervalMs / 1000, () =>
        setIndex((current) => (current + 1) % total)
      )
    },
    {
      scope: rootRef,
      dependencies: [index, total, motionOk, paused, held, intervalMs],
      // Cancels the pending advance whenever the carousel is held, paused or
      // moved on.
      revertOnUpdate: true,
    }
  )

  const autoplaying = motionOk && !paused

  return (
    <section
      ref={rootRef}
      aria-roledescription="carousel"
      aria-label={label}
      className={cn("flex flex-col gap-3", className)}
      {...props}
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      onFocus={() => setHeld(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setHeld(false)
      }}
    >
      <div
        className="overflow-hidden rounded-3xl"
        onPointerDown={(event) => {
          if (event.pointerType !== "mouse") swipeRef.current = event.clientX
        }}
        onPointerUp={(event) => {
          const start = swipeRef.current
          swipeRef.current = null
          if (start === null) return
          const dx = event.clientX - start
          if (Math.abs(dx) < SWIPE_PX) return
          const rtl = getComputedStyle(event.currentTarget).direction === "rtl"
          // Swiping toward the reading start brings in the next slide.
          go(index + (dx < 0 !== rtl ? 1 : -1))
        }}
      >
        <div
          ref={trackRef}
          aria-live={autoplaying ? "off" : "polite"}
          className="flex items-stretch"
        >
          {slides.map((slide, i) => (
            <div
              key={i}
              role="group"
              aria-roledescription="slide"
              aria-label={labels.slide
                .replace("{index}", String(i + 1))
                .replace("{total}", String(total))}
              inert={i !== index}
              className="w-full shrink-0 [&>article]:h-full"
            >
              {slide}
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-center gap-2">
        <CarouselButton label={labels.previous} onClick={() => go(index - 1)}>
          <ChevronLeftIcon className="size-4 rtl:rotate-180" aria-hidden />
        </CarouselButton>
        <div className="flex items-center gap-1.5">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={labels.goTo.replace("{index}", String(i + 1))}
              aria-current={i === index ? "true" : undefined}
              onClick={() => go(i)}
              className={cn(
                "h-2 rounded-full transition-all focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none",
                i === index
                  ? "w-6 bg-primary"
                  : "w-2 bg-muted-foreground/40 hover:bg-muted-foreground/70"
              )}
            />
          ))}
        </div>
        {motionOk ? (
          <CarouselButton
            label={paused ? labels.play : labels.pause}
            onClick={() => setPaused((p) => !p)}
          >
            {paused ? (
              <PlayIcon className="size-4" aria-hidden />
            ) : (
              <PauseIcon className="size-4" aria-hidden />
            )}
          </CarouselButton>
        ) : null}
        <CarouselButton label={labels.next} onClick={() => go(index + 1)}>
          <ChevronRightIcon className="size-4 rtl:rotate-180" aria-hidden />
        </CarouselButton>
      </div>
    </section>
  )
}

function CarouselButton({
  label,
  onClick,
  children,
}: {
  label: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="inline-flex size-9 items-center justify-center rounded-full border bg-background text-foreground hover:bg-muted focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none"
    >
      {children}
    </button>
  )
}
